import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Truck, Loader2, Trash2, Eye, CheckCircle, Clock,
  XCircle, Package, User, Building2, ArrowUpFromLine,
  ArrowDownToLine, ChevronRight, X, Calendar, MapPin
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  revoked: 'bg-red-50 text-red-600 border-red-200',
};

const ManageDeliveryBoys = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [boys, setBoys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  const [assignTarget, setAssignTarget] = useState(null); // delivery boy to assign
  const [assignOrderType, setAssignOrderType] = useState('pg');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignType, setAssignType] = useState('pickup');
  const [assignDate, setAssignDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [viewTarget, setViewTarget] = useState(null); // delivery boy to view tasks
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const fetchBoys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/delivery-boys`, { headers });
      setBoys(res.data);
    } catch { toast.error('Failed to load delivery boys'); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBoys(); }, [fetchBoys]);

  // Create delivery boy
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, {
        name: createForm.name,
        email: createForm.email,
        password: createForm.password,
        role: 'delivery_boy',
      }, { headers });
      toast.success(`Delivery boy ${createForm.name} created`);
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '' });
      fetchBoys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setCreating(false); }
  };

  // Delete delivery boy
  const handleDelete = async (boy) => {
    if (!window.confirm(`Delete delivery boy "${boy.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${boy.id}`, { headers });
      toast.success('Deleted');
      fetchBoys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Open assign modal
  const openAssign = (boy) => {
    setAssignTarget(boy);
    setAssignOrderType('pg');
    setSelectedOrder(null);
    setAssignType('pickup');
    setAssignDate(new Date().toISOString().split('T')[0]);
    setAssignNotes('');
    setOrders([]);
  };

  // Fetch orders for assignment
  const fetchOrdersForAssign = async (type) => {
    setOrdersLoading(true);
    setSelectedOrder(null);
    try {
      const url = type === 'pg'
        ? `${API}/admin/customer-orders`
        : `${API}/admin/individual-orders`;
      const res = await axios.get(url, { headers });
      const allOrders = res.data?.orders || res.data || [];
      const active = allOrders.filter(o => o.status !== 'delivered' && o.status !== 'revoked');
      setOrders(active);
    } catch { toast.error('Failed to load orders'); }
    finally { setOrdersLoading(false); }
  };

  // Submit assignment
  const handleAssign = async () => {
    if (!selectedOrder) { toast.error('Select an order'); return; }
    setAssigning(true);
    try {
      await axios.post(`${API}/admin/delivery-assignments`, {
        delivery_boy_id: assignTarget.id,
        order_id: selectedOrder.id,
        order_type: assignOrderType,
        assignment_type: assignType,
        scheduled_date: assignDate,
        notes: assignNotes || null,
      }, { headers });
      toast.success('Assignment created!');
      setAssignTarget(null);
      fetchBoys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  // View assignments
  const openView = async (boy) => {
    setViewTarget(boy);
    setAssignmentsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/delivery-assignments?delivery_boy_id=${boy.id}`, { headers });
      setAssignments(res.data);
    } catch { toast.error('Failed to load assignments'); }
    finally { setAssignmentsLoading(false); }
  };

  // Revoke
  const handleRevoke = async (id) => {
    setRevoking(id);
    try {
      await axios.patch(`${API}/admin/delivery-assignments/${id}/revoke`, {}, { headers });
      toast.success('Revoked');
      setAssignments(prev => prev.map(a => a.id === id ? { ...a, status: 'revoked' } : a));
      fetchBoys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    } finally { setRevoking(null); }
  };

  const totalPending = boys.reduce((s, b) => s + (b.pending_count || 0), 0);

  return (
    <div className="space-y-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Delivery Boys</h1>
          <p className="text-sm text-slate-400 mt-1">Manage delivery staff and assign pickups/deliveries</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm shadow-md shadow-blue-200 transition-all hover:-translate-y-0.5"
        >
          <Plus className="w-4 h-4" /> Add Delivery Boy
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Staff', value: boys.length, color: 'blue' },
          { label: 'Pending Assignments', value: totalPending, color: 'amber' },
          { label: 'Active', value: boys.filter(b => b.is_active !== false).length, color: 'emerald' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm border-l-4 border-l-${color}-500`}>
            <p className="text-2xl font-black text-slate-800">{value}</p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Delivery boys list */}
      <div className="bg-white border border-slate-200/80 rounded-[2rem] overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Staff</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : boys.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center px-8">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
              <Truck className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
            </div>
            <p className="font-bold text-slate-500">No delivery boys yet</p>
            <p className="text-sm text-slate-400">Click "Add Delivery Boy" to create the first one</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {boys.map(boy => (
              <div key={boy.id} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0 shadow-sm shadow-blue-200">
                  {boy.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{boy.name}</p>
                  <p className="text-xs text-slate-400 truncate">{boy.email}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {boy.pending_count > 0 && (
                    <span className="text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                      {boy.pending_count} pending
                    </span>
                  )}
                  {boy.completed_count > 0 && (
                    <span className="text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                      {boy.completed_count} done
                    </span>
                  )}
                  <button
                    onClick={() => openView(boy)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Tasks
                  </button>
                  <button
                    onClick={() => openAssign(boy)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors shadow-sm shadow-emerald-200"
                  >
                    <Plus className="w-3.5 h-3.5" /> Assign
                  </button>
                  <button
                    onClick={() => handleDelete(boy)}
                    className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Delivery Boy</h3>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {[
                { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Ravi Kumar' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'ravi@justfold.in' },
                { label: 'Password', key: 'password', type: 'password', placeholder: '••••••••' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
                  <input
                    type={type}
                    placeholder={placeholder}
                    value={createForm[key]}
                    onChange={e => setCreateForm(p => ({ ...p, [key]: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={creating}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm transition-all"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Delivery Boy
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Assign Order modal ───────────────────────────────────────────────── */}
      {assignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Assign Order
                </h3>
                <p className="text-sm text-slate-400">for {assignTarget.name}</p>
              </div>
              <button onClick={() => setAssignTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Order Type */}
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Order Type</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'pg', label: 'PG Customer', icon: Building2 },
                    { key: 'individual', label: 'Individual', icon: User },
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => { setAssignOrderType(key); setSelectedOrder(null); setOrders([]); }}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-bold transition-all ${
                        assignOrderType === key
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Load orders button */}
              {orders.length === 0 && !ordersLoading && (
                <button
                  onClick={() => fetchOrdersForAssign(assignOrderType)}
                  className="w-full py-2.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-bold hover:bg-blue-100 transition-colors"
                >
                  Load {assignOrderType === 'pg' ? 'PG' : 'Individual'} Orders
                </button>
              )}
              {ordersLoading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>}

              {/* Orders list */}
              {orders.length > 0 && (
                <div>
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Select Order</p>
                  <div className="border border-slate-200 rounded-2xl overflow-hidden max-h-52 overflow-y-auto divide-y divide-slate-50">
                    {orders.map(order => (
                      <button
                        key={order.id}
                        onClick={() => setSelectedOrder(order)}
                        className={`w-full text-left px-4 py-3 transition-colors flex items-center gap-3 ${
                          selectedOrder?.id === order.id
                            ? 'bg-blue-50 border-l-2 border-l-blue-600'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">
                            {order.customer_name || order.full_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {order.total_clothes != null ? `${order.total_clothes} items · ` : ''}
                            {format(new Date(order.order_date || order.created_at), 'dd MMM yyyy')} · <span className="capitalize">{order.status}</span>
                          </p>
                        </div>
                        {selectedOrder?.id === order.id && <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Assignment type */}
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Assignment Type</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'pickup', label: 'Pickup', icon: ArrowUpFromLine, color: 'blue' },
                    { key: 'delivery', label: 'Delivery', icon: ArrowDownToLine, color: 'emerald' },
                  ].map(({ key, label, icon: Icon, color }) => (
                    <button
                      key={key}
                      onClick={() => setAssignType(key)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-bold transition-all ${
                        assignType === key
                          ? (color === 'blue' ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200' : 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200')
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scheduled date */}
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Scheduled Date</label>
                <input
                  type="date"
                  value={assignDate}
                  onChange={e => setAssignDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Notes (optional)</label>
                <input
                  type="text"
                  placeholder="Any special instructions..."
                  value={assignNotes}
                  onChange={e => setAssignNotes(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                />
              </div>

              <button
                onClick={handleAssign}
                disabled={assigning || !selectedOrder}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl text-sm shadow-md shadow-emerald-200 transition-all"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                Assign Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Assignments modal ───────────────────────────────────────────── */}
      {viewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {viewTarget.name}'s Assignments
                </h3>
                <p className="text-sm text-slate-400">{assignments.length} total</p>
              </div>
              <button onClick={() => setViewTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {assignmentsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
            ) : assignments.length === 0 ? (
              <div className="py-10 text-center">
                <p className="font-bold text-slate-500">No assignments yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map(a => (
                  <div key={a.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className={`px-4 py-2.5 flex items-center justify-between ${
                      a.assignment_type === 'pickup' ? 'bg-blue-50' : 'bg-emerald-50'
                    }`}>
                      <div className="flex items-center gap-2">
                        {a.assignment_type === 'pickup'
                          ? <ArrowUpFromLine className="w-3.5 h-3.5 text-blue-600" />
                          : <ArrowDownToLine className="w-3.5 h-3.5 text-emerald-600" />}
                        <span className={`text-xs font-black uppercase ${
                          a.assignment_type === 'pickup' ? 'text-blue-700' : 'text-emerald-700'
                        }`}>{a.assignment_type} — {a.order_type === 'pg' ? 'PG' : 'Individual'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">{a.scheduled_date}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_BADGE[a.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {a.status}
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-slate-700">{a.order?.customer_name || 'Order'}</p>
                        <p className="text-xs text-slate-400">
                          {a.order_type === 'pg' ? (a.order?.pg_name || a.order?.pg_address) : a.order?.address_line1}
                        </p>
                      </div>
                      {a.status === 'pending' && (
                        <button
                          onClick={() => handleRevoke(a.id)}
                          disabled={revoking === a.id}
                          className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-100"
                        >
                          {revoking === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDeliveryBoys;
